import React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import SLUGS from '../../resources/slugs';
import {
  IconAgents,
  IconArticles,
  IconContacts,
  IconIdeas,
  IconLogout,
  IconOverview,
  IconSettings,
  IconSubscription,
  IconTickets,
} from '../../assets/icons';
import { convertSlugToUrl } from '../../resources/utilities';
import { AuthContext } from '../../context/logeduser';

import Menu from './MenuComponent';
import MenuItem from './MenuItemComponent';

const useStyles = createUseStyles({
  separator: {
    borderTop: ({ theme }) => `1px solid ${theme.color.lightGrayishBlue}`,
    marginTop: 16,
    marginBottom: 16,
    opacity: 0.06,
  },
});

function SidebarComponent() {
  const { authData } = React.useContext(AuthContext);
  const isGlobalAdmin = !authData?.admin?.is_restricted && authData?.admin?.email === 'admin@atshub.in';
  const navigate = useNavigate();
  const theme = useTheme();
  const classes = useStyles({ theme });
  const isMobile = window.innerWidth <= 1080;

  async function logout() {
    navigate(SLUGS.login);
  }

  function onClick(slug, parameters = {}) {
    navigate(convertSlugToUrl(slug, parameters));
  }

  return (
    <Menu isMobile={isMobile}>
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b', letterSpacing: '-1px' }}>
          SMART <span style={{ color: '#3DAC5A' }}>HR</span>
        </Typography>
      </Box>
      <div style={{ paddingTop: 10 }}></div>
      <MenuItem
        id={SLUGS.dashboard}
        title="Dashboard"
        icon={IconSubscription}
        onClick={() => onClick(SLUGS.dashboard)}
      />
      <MenuItem
        id={SLUGS.overview}
        items={[SLUGS.overviewTwo, SLUGS.overviewThree]}
        title="Overview"
        icon={IconOverview}
      >
        <MenuItem
          id={SLUGS.overview}
          title="Sub Item 1"
          level={2}
          icon={IconAgents}
          onClick={() => onClick(SLUGS.overview)}
        />
        <MenuItem
          id={SLUGS.overviewTwo}
          title="Sub Item 2"
          level={2}
          icon={IconContacts}
          onClick={() => onClick(SLUGS.overviewTwo)}
        />
        <MenuItem
          id={SLUGS.overviewThree}
          title="Sub Item 3"
          level={2}
          icon={IconArticles}
          onClick={() => onClick(SLUGS.overviewThree)}
        />
      </MenuItem>
      <MenuItem
        id={SLUGS.tickets}
        title="Tickets"
        icon={IconTickets}
        onClick={() => onClick(SLUGS.tickets)}
      />
      <MenuItem
        id={SLUGS.ideas}
        items={[SLUGS.ideasTwo, SLUGS.ideasThree]}
        title="Ideas"
        icon={IconIdeas}
      >
        <MenuItem
          id={SLUGS.ideas}
          title="Sub Item 1"
          level={2}
          icon={IconAgents}
          onClick={() => onClick(SLUGS.ideas)}
        />
        <MenuItem
          id={SLUGS.ideasTwo}
          title="Sub Item 2"
          level={2}
          icon={IconContacts}
          onClick={() => onClick(SLUGS.ideasTwo)}
        />
        <MenuItem
          id={SLUGS.ideasThree}
          title="Sub Item 3"
          level={2}
          icon={IconArticles}
          onClick={() => onClick(SLUGS.ideasThree)}
        />
      </MenuItem>
      <MenuItem
        id={SLUGS.contacts}
        title="Contacts"
        icon={IconContacts}
        onClick={() => onClick(SLUGS.contacts)}
      />
      <MenuItem
        id={SLUGS.agents}
        title="Agents"
        icon={IconAgents}
        onClick={() => onClick(SLUGS.agents)}
      />
      <MenuItem
        id={SLUGS.articles}
        title="Articles"
        icon={IconArticles}
        onClick={() => onClick(SLUGS.articles)}
      />
      <MenuItem
        id={SLUGS.leaves}
        items={[SLUGS.leaveTracker, SLUGS.teamLeaves]}
        title="Leaves"
        icon={IconTickets}
      >
        <MenuItem
          id={SLUGS.leaveTracker}
          title="Leave Tracker"
          level={2}
          icon={IconTickets}
          onClick={() => onClick(SLUGS.leaveTracker)}
        />
        <MenuItem
          id={SLUGS.teamLeaves}
          title="Team Leaves"
          level={2}
          icon={IconAgents}
          onClick={() => onClick(SLUGS.teamLeaves)}
        />
      </MenuItem>
      <MenuItem
        id="attendance"
        items={[SLUGS.attendancePresent]}
        title="Attendance"
        icon={IconOverview}
      >
        <MenuItem
          id={SLUGS.attendancePresent}
          title="Daily Status"
          level={2}
          icon={IconAgents}
          onClick={() => onClick(SLUGS.attendancePresent)}
        />
      </MenuItem>
      <MenuItem
        id={SLUGS.payroll}
        items={[SLUGS.payrollProcess, SLUGS.payslipLogs, SLUGS.bulkSalary, SLUGS.detailedSalaryReport]}
        title="Payroll"
        icon={IconTickets}
      >
        <MenuItem
          id={SLUGS.payrollProcess}
          title="Payroll Process"
          level={2}
          icon={IconTickets}
          onClick={() => onClick(SLUGS.payrollProcess)}
        />
        <MenuItem
          id={SLUGS.payslipLogs}
          title="Payslip Logs"
          level={2}
          icon={IconTickets}
          onClick={() => onClick(SLUGS.payslipLogs)}
        />
        <MenuItem
          id={SLUGS.payroll}
          title="Salary Management"
          level={2}
          icon={IconTickets}
          onClick={() => onClick(SLUGS.payroll)}
        />
        <MenuItem
          id={SLUGS.bulkSalary}
          title="Bulk Salary Update"
          level={2}
          icon={IconTickets}
          onClick={() => onClick(SLUGS.bulkSalary)}
        />
        <MenuItem
          id={SLUGS.detailedSalaryReport}
          title="Detailed Salary Report"
          level={2}
          icon={IconTickets}
          onClick={() => onClick(SLUGS.detailedSalaryReport)}
        />
      </MenuItem>
      <MenuItem
        id={SLUGS.subscription}
        title="Subscription"
        icon={IconSubscription}
        onClick={() => onClick(SLUGS.subscription)}
      />
      <div className={classes.separator}></div>
      <MenuItem
        id={SLUGS.settings}
        items={[SLUGS.masterData, SLUGS.activityLogs]}
        title="Settings"
        icon={IconSettings}
      >
        <MenuItem
          id={SLUGS.settings}
          title="Profile"
          level={2}
          icon={IconSettings}
          onClick={() => onClick(SLUGS.settings)}
        />
        <MenuItem
          id={SLUGS.masterData}
          title="Master Data"
          level={2}
          icon={IconOverview}
          onClick={() => onClick(SLUGS.masterData)}
        />
        {isGlobalAdmin && <MenuItem
          id={SLUGS.activityLogs}
          title="Activity Logs"
          level={2}
          icon={IconArticles}
          onClick={() => onClick(SLUGS.activityLogs)}
        />}
      </MenuItem>

      <MenuItem id="logout" title="Logout" icon={IconLogout} onClick={logout} />
      <Box sx={{ mt: 'auto', p: 3, opacity: 0.5 }}>
        <Typography variant="caption" sx={{ color: '#666', fontSize: '0.65rem' }}>
          v2.0 Redesign Portal
        </Typography>
      </Box>
    </Menu>
  );
}

export default SidebarComponent;
